/**
 * Multi-Cashier Shifts — header badge + open/close dialogs.
 *
 * "End" button navigates to /close-shift?shift_id=... (full 7-step wizard page)
 * instead of opening an inline modal.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Clock, ClipboardCheck, AlertCircle, LogOut, Banknote, AlertTriangle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import CashDropDialog from './CashDropDialog';

export default function ActiveShiftBadge({ branchId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState({ enabled: false, active_shift: null, requires_open: false });
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dropDialog, setDropDialog] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isSalesPage = location.pathname.startsWith('/sales');
  const shouldAutoBlock = status.enabled && status.requires_open && (!isAdmin || isSalesPage);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/shifts/feature-status');
      setStatus(res.data);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  useEffect(() => {
    if (shouldAutoBlock) {
      setOpenDialog(true);
    } else if (!status.requires_open) {
      setOpenDialog(false);
    }
  }, [shouldAutoBlock, status.requires_open]);

  if (loading || !status.enabled) return null;

  const shift = status.active_shift;

  const handleEndShift = () => {
    if (shift?.shift_id) {
      navigate(`/close-shift?shift_id=${shift.shift_id}`);
    }
  };

  return (
    <>
      {shift ? (
        <div className="flex items-center gap-1.5">
          <div
            data-testid="active-shift-badge"
            className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-xs"
            title={`Shift ${shift.shift_id} · Opened ${formatTime(shift.opened_at)} · Float ₱${(shift.opening_float || 0).toLocaleString()}`}
          >
            <ClipboardCheck size={13} className="text-emerald-700" />
            <span className="font-medium text-emerald-800">{shift.shift_id}</span>
            <span className="text-emerald-700/70">·</span>
            <span className="text-emerald-700/70">₱{(shift.opening_float || 0).toLocaleString()}</span>
          </div>
          <button
            data-testid="drop-shift-trigger"
            onClick={() => setDropDialog(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-emerald-200 text-emerald-800 text-xs font-medium hover:bg-emerald-50"
            title="Record a mid-shift cash drop"
          >
            <Banknote size={12} />
            Drop
          </button>
          <button
            data-testid="end-shift-trigger"
            onClick={handleEndShift}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-emerald-200 text-emerald-800 text-xs font-medium hover:bg-emerald-50"
            title="End shift & generate X-Report"
          >
            <LogOut size={12} />
            End
          </button>
        </div>
      ) : status.requires_open ? (
        <button
          data-testid="open-shift-trigger"
          onClick={() => setOpenDialog(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium hover:bg-amber-100"
        >
          <AlertCircle size={13} />
          Open Shift
        </button>
      ) : null}

      <OpenShiftDialog
        open={openDialog}
        onOpenChange={(o) => setOpenDialog(o)}
        branchId={branchId}
        onOpened={() => {
          setOpenDialog(false);
          fetchStatus();
        }}
        blocker={status.requires_open}
      />

      <CashDropDialog
        open={dropDialog}
        onOpenChange={setDropDialog}
        shiftId={shift?.shift_id}
        onDropped={() => fetchStatus()}
      />
    </>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(11, 16);
  }
}

function OpenShiftDialog({ open, onOpenChange, branchId, onOpened, blocker }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openingFloat, setOpeningFloat] = useState('');
  const [source, setSource] = useState('main_drawer');
  const [managerPin, setManagerPin] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mainDrawerBalance, setMainDrawerBalance] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const noPinConfigured = !user?.has_manager_pin && !isAdmin;
  const adminHasNoPin = isAdmin && !user?.has_manager_pin;

  // Fetch the main cashier wallet balance so manager can see available funds
  useEffect(() => {
    if (!open || !branchId) return;
    api.get('/shifts/cashier-drawer-balance', { params: { branch_id: branchId } })
      .then(res => setMainDrawerBalance(res.data?.main_only_balance ?? null))
      .catch(() => setMainDrawerBalance(null));
  }, [open, branchId]);

  useEffect(() => {
    if (!open) {
      setOpeningFloat('');
      setSource('main_drawer');
      setManagerPin('');
      setNote('');
      setMainDrawerBalance(null);
    }
  }, [open]);

  const submit = async () => {
    if (!branchId) { toast.error('No branch selected.'); return; }
    if (!managerPin.trim()) { toast.error('Manager PIN is required.'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/shifts/open', {
        branch_id: branchId,
        opening_float: Number(openingFloat) || 0,
        source,
        manager_pin: managerPin.trim(),
        note: note.trim() || undefined,
      });
      toast.success(`Shift ${res.data.shift_id} opened.`);
      onOpened?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'insufficient_main_drawer') {
        toast.error(
          `${detail.message} Shortfall: ₱${detail.shortfall?.toLocaleString(undefined,{minimumFractionDigits:2})}. ${detail.hint}`,
          { duration: 8000 }
        );
      } else {
        const msg = typeof detail === 'string' ? detail : detail?.message || 'Failed to open shift';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    toast.warning('Shift not opened — sales will be blocked until a shift is started.');
    onOpenChange(false);
  };

  const handleGoToSettings = () => {
    onOpenChange(false);
    navigate('/settings');
    toast.info('Go to Settings → PINs to set up a manager PIN, then open a shift.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="open-shift-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={18} className="text-emerald-700" />
            Open Cashier Shift
          </DialogTitle>
          <DialogDescription>
            {blocker
              ? 'Sales are locked until a shift is opened. A manager or admin PIN is required.'
              : 'Start a new accountable shift for this cashier.'}
          </DialogDescription>
        </DialogHeader>

        {(noPinConfigured || adminHasNoPin) && (
          <div
            data-testid="no-pin-warning"
            className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
          >
            <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800 space-y-1">
              {adminHasNoPin ? (
                <>
                  <p className="font-semibold">No manager PIN is set for this account.</p>
                  <p>Set a manager or admin PIN in <strong>Settings → PINs</strong> before opening a shift.</p>
                </>
              ) : (
                <>
                  <p className="font-semibold">No manager PIN on your account.</p>
                  <p>Ask your manager or admin to authorize this shift.</p>
                </>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="opening-float">Opening Float (₱)</Label>
            <Input
              id="opening-float"
              data-testid="opening-float-input"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
            />
            <p className="text-[11px] text-slate-500">Leave 0 if starting with an empty drawer.</p>
          </div>

          <div className="space-y-2">
            <Label>Source of Float</Label>
            <RadioGroup value={source} onValueChange={setSource}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="main_drawer" id="src-main" data-testid="src-main" />
                <Label htmlFor="src-main" className="font-normal cursor-pointer">
                  Main Cashier Drawer
                  <span className="ml-1.5 text-[10px] text-slate-400">— deducted from drawer</span>
                </Label>
              </div>
              {/* Show available balance when main_drawer is selected */}
              {source === 'main_drawer' && mainDrawerBalance !== null && (
                <div className={`ml-6 flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
                  mainDrawerBalance < (Number(openingFloat) || 0)
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                }`} data-testid="main-drawer-balance">
                  <Banknote size={11} />
                  <span>
                    Available: <strong>₱{mainDrawerBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                    {mainDrawerBalance < (Number(openingFloat) || 0) && (
                      <span className="ml-1 font-semibold">— insufficient for this float</span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <RadioGroupItem value="safe" id="src-safe" data-testid="src-safe" />
                <Label htmlFor="src-safe" className="font-normal cursor-pointer">
                  Safe
                  <span className="ml-1.5 text-[10px] text-slate-400">— deducted from safe</span>
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="owner_handoff" id="src-owner" data-testid="src-owner" />
                <Label htmlFor="src-owner" className="font-normal cursor-pointer">
                  Owner Hand-off
                  <span className="ml-1.5 text-[10px] text-emerald-600">— external cash injection</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="open-note">Note (optional)</Label>
            <Input
              id="open-note"
              data-testid="open-note-input"
              placeholder="e.g., backup cashier, lunch coverage"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manager-pin">Manager / Admin PIN</Label>
            <Input
              id="manager-pin"
              data-testid="manager-pin-input"
              type="password"
              placeholder="Required to authorize float"
              value={managerPin}
              onChange={(e) => setManagerPin(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !submitting && managerPin.trim() && submit()}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex gap-2">
            {adminHasNoPin ? (
              <Button
                variant="outline"
                size="sm"
                data-testid="go-to-settings-btn"
                onClick={handleGoToSettings}
                className="text-amber-700 border-amber-300 hover:bg-amber-50 gap-1.5"
              >
                <Settings size={13} />
                Go to Settings
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                data-testid="exit-shift-dialog-btn"
                onClick={handleExit}
                className="text-slate-500 hover:text-slate-700"
              >
                Exit for now
              </Button>
            )}
          </div>
          <Button
            data-testid="confirm-open-shift"
            onClick={submit}
            disabled={submitting || !managerPin.trim()}
            className="bg-emerald-700 hover:bg-emerald-800"
          >
            {submitting ? 'Opening…' : 'Open Shift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
